// /////////////////////////////////////////////////////////////
// [ファイル] CustomerContactList.tsx
// [概要] 個人顧客の複数連絡先 (trn_customer_contacts) をスコープ別に表示・編集するリスト。
//        - scope='personal' は「連絡先」セクション、scope='business' は「勤務先・担当情報」
//          セクションに配置し、props の contacts (全スコープ) から自スコープ分だけを扱う。
//        - 種別 (固定電話 / 携帯 / FAX / メール) × ラベル × 値 × 代表フラグ。
//          代表は (scope, 種別) につき 1 件で、サーバー側で個人顧客の連絡先 6 列へ同期される。
//        - 行編集の state は本コンポーネント内に閉じ、確定時 (blur / 削除 / 代表切替 / 種別変更)
//          のみ onChange で親へ渡す (キー入力ごとの親再描画を避ける)。
// /////////////////////////////////////////////////////////////
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  Link,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import * as Icons from '../constants/icons';
import { STANDARD_TEXT_BUTTON_SX } from '../constants/buttonStyles';
import { validateContactValue } from '../utils/validation';

export type ContactScope = 'personal' | 'business';
export type ContactKind = 'tel' | 'mobile' | 'fax' | 'email';

export interface CustomerContact {
  id?: number | null;
  scope: ContactScope;
  contact_kind: ContactKind;
  label?: string | null;
  value: string;
  is_primary: boolean;
  sort_no?: number;
  remarks?: string | null;
  // 編集中の行識別用 (保存対象外)
  _key?: string;
}

export const CONTACT_KIND_OPTIONS: { value: ContactKind; label: string; placeholder: string }[] = [
  { value: 'tel', label: '固定電話', placeholder: '例: 092-123-4567' },
  { value: 'mobile', label: '携帯', placeholder: '例: 090-1234-5678' },
  { value: 'fax', label: 'FAX', placeholder: '例: 092-123-4568' },
  { value: 'email', label: 'メール', placeholder: '例: taro@example.com' },
];

const kindLabel = (kind: ContactKind) => CONTACT_KIND_OPTIONS.find((o) => o.value === kind)?.label || kind;

const KindIcon: React.FC<{ kind: ContactKind }> = ({ kind }) => {
  const sx = { fontSize: 15, color: 'text.disabled' };
  switch (kind) {
    case 'mobile':
      return <Icons.Smartphone sx={sx} />;
    case 'fax':
      return <Icons.Fax sx={sx} />;
    case 'email':
      return <Icons.EmailOutlined sx={sx} />;
    default:
      return <Icons.PhoneOutlined sx={sx} />;
  }
};

let keySeq = 0;
const nextKey = () => `c${Date.now()}_${keySeq++}`;

// 親から渡された行に編集用キーを付与する (id があれば id を使う)
const withKeys = (rows: CustomerContact[]): CustomerContact[] =>
  rows.map((r) => ({ ...r, _key: r._key || (r.id != null ? `id${r.id}` : nextKey()) }));

interface CustomerContactListProps {
  scope: ContactScope;
  contacts: CustomerContact[];           // 全スコープの連絡先
  onChange?: (contacts: CustomerContact[]) => void;  // 全スコープの連絡先を返す
  readOnly?: boolean;
  error?: string;                        // 親側バリデーションのメッセージ (任意)
  // 選択できる種別を絞る (法人は電話のみ。未指定なら全種別)
  kinds?: ContactKind[];
}

const CustomerContactList: React.FC<CustomerContactListProps> = ({
  scope,
  contacts,
  onChange,
  readOnly = false,
  error,
  kinds,
}) => {
  const kindOptions = kinds
    ? CONTACT_KIND_OPTIONS.filter((o) => kinds.includes(o.value))
    : CONTACT_KIND_OPTIONS;
  const others = (contacts || []).filter((c) => c.scope !== scope);
  const [rows, setRows] = useState<CustomerContact[]>(() =>
    withKeys((contacts || []).filter((c) => c.scope === scope)),
  );

  // 親の contacts が差し替わった時 (詳細再取得・種別変換など) はローカル行を同期する
  useEffect(() => {
    setRows(withKeys((contacts || []).filter((c) => c.scope === scope)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts, scope]);

  // 確定時に親へ通知 (他スコープの行はそのまま連結)
  const commit = useCallback(
    (next: CustomerContact[]) => {
      setRows(next);
      onChange?.([...others, ...next.map((r, i) => ({ ...r, scope, sort_no: i }))]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [others, onChange, scope],
  );

  // 代表は (scope, 種別) につき 1 件。無ければ先頭を代表にする
  const normalizePrimary = (list: CustomerContact[]): CustomerContact[] => {
    const seen = new Set<ContactKind>();
    const out = list.map((r) => {
      if (r.is_primary) {
        if (seen.has(r.contact_kind)) return { ...r, is_primary: false };
        seen.add(r.contact_kind);
      }
      return r;
    });
    return out.map((r) => {
      if (!seen.has(r.contact_kind)) {
        seen.add(r.contact_kind);
        return { ...r, is_primary: true };
      }
      return r;
    });
  };

  const updateRow = (key: string, patch: Partial<CustomerContact>, commitNow: boolean) => {
    const next = rows.map((r) => (r._key === key ? { ...r, ...patch } : r));
    if (commitNow) commit(normalizePrimary(next));
    else setRows(next);
  };

  const setPrimary = (key: string) => {
    const target = rows.find((r) => r._key === key);
    if (!target) return;
    const next = rows.map((r) =>
      r.contact_kind === target.contact_kind ? { ...r, is_primary: r._key === key } : r,
    );
    commit(next);
  };

  const removeRow = (key: string) => {
    commit(normalizePrimary(rows.filter((r) => r._key !== key)));
  };

  const addRow = () => {
    const preferred: ContactKind = scope === 'business' ? 'tel' : 'mobile';
    const kind: ContactKind = kindOptions.some((o) => o.value === preferred)
      ? preferred
      : (kindOptions[0]?.value ?? preferred);
    const hasPrimary = rows.some((r) => r.contact_kind === kind && r.is_primary);
    setRows([
      ...rows,
      { _key: nextKey(), scope, contact_kind: kind, label: '', value: '', is_primary: !hasPrimary },
    ]);
  };

  // ============================
  // 表示モード
  // ============================
  if (readOnly) {
    if (rows.length === 0) {
      return (
        <Typography variant="body2" color="text.disabled" sx={{ mt: 0.25, minHeight: 20 }}>
          -
        </Typography>
      );
    }
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {rows.map((r) => (
          <Box key={r._key} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <KindIcon kind={r.contact_kind} />
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 48, fontSize: '0.7rem' }}>
              {kindLabel(r.contact_kind)}
            </Typography>
            {r.label && (
              <Chip label={r.label} size="small" sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }} />
            )}
            {r.contact_kind === 'email' ? (
              <Link
                href={`https://mail.google.com/mail/?view=cm&fs=1&to=${r.value}`}
                target="_blank"
                rel="noopener"
                variant="body2"
                sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, wordBreak: 'break-all' }}
              >
                {r.value}
              </Link>
            ) : (
              // 電話・FAX ともテキスト表示。tel: の発信リンクはスマホ展開・CTI 連携が
              // 未整備のため設けない (2026-09-17 ユーザー指示)
              <Typography variant="body2">{r.value}</Typography>
            )}
            {r.is_primary && (
              <Tooltip title="代表連絡先 (一覧・連携先で使用)">
                <Chip label="代表" size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }} />
              </Tooltip>
            )}
          </Box>
        ))}
      </Box>
    );
  }

  // ============================
  // 編集モード
  // ============================
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {rows.map((r) => {
        const opt = CONTACT_KIND_OPTIONS.find((o) => o.value === r.contact_kind);
        const rowError = validateContactValue(r.contact_kind, r.value);
        return (
          <Box
            key={r._key}
            sx={{
              display: 'grid',
              // カテゴリ・ラベルが 3 点リーダー化しないよう固定幅をやめ、3 列を比率で配分する
              gridTemplateColumns: {
                xs: '1fr 1fr',
                sm: 'minmax(150px, 1fr) minmax(170px, 1.2fr) minmax(200px, 1.6fr) auto auto',
              },
              gap: 1,
              alignItems: 'start',
            }}
          >
            <TextField
              select
              size="small"
              value={r.contact_kind}
              onChange={(e) => updateRow(r._key!, { contact_kind: e.target.value as ContactKind }, true)}
            >
              {kindOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              placeholder={scope === 'business' ? 'ラベル (例: 直通)' : 'ラベル (例: 自宅)'}
              value={r.label || ''}
              onChange={(e) => updateRow(r._key!, { label: e.target.value }, false)}
              onBlur={() => commit(rows)}
              inputProps={{ maxLength: 50 }}
            />
            <TextField
              size="small"
              placeholder={opt?.placeholder}
              value={r.value}
              onChange={(e) => updateRow(r._key!, { value: e.target.value }, false)}
              onBlur={() => commit(rows)}
              error={!!rowError}
              helperText={rowError || undefined}
              inputProps={{ maxLength: 255 }}
            />
            <FormControlLabel
              sx={{ m: 0, whiteSpace: 'nowrap', '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
              control={
                <Checkbox
                  size="small"
                  checked={!!r.is_primary}
                  onChange={() => setPrimary(r._key!)}
                  icon={<Icons.StarBorder fontSize="small" />}
                  checkedIcon={<Icons.Star fontSize="small" color="primary" />}
                />
              }
              label="代表"
            />
            <IconButton size="small" onClick={() => removeRow(r._key!)} title="削除">
              <Icons.Delete sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        );
      })}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button size="small" startIcon={<Icons.Add />} onClick={addRow} sx={STANDARD_TEXT_BUTTON_SX}>
          連絡先を追加
        </Button>
        {error && (
          <Typography variant="caption" color="error">{error}</Typography>
        )}
      </Box>
    </Box>
  );
};

export default CustomerContactList;
